/**
 * SMBeamDesigner Constants
 * 
 * Default values, material presets, and configuration constants
 */

import { MaterialPresets, CrossSection } from './types'

// Bootstrap material properties: E (Pa) and density (kg/m³) used only as initial display values
// before the backend resolves the selected material from the DB. Partial factors and reference
// strengths are NOT defined here — they are DB-authoritative and fetched from the /design endpoints
// (see useMaterialState.materialFactorsDisplay), so the materials UI can never drift from the DB.
export const MATERIAL_PRESETS: MaterialPresets = {
  steel: {
    name: 'S355 (Steel)',
    E: 210e9,  // 210 GPa
    density: 7850  // kg/m³
  },
  concrete: {
    name: 'C30/37 (Concrete)',
    E: 33e9,  // 33 GPa
    density: 2500  // kg/m³
  },
  timber: {
    name: 'GL24h (Glulam)',
    E: 11.5e9,  // 11.5 GPa
    density: 420  // kg/m³
  }
}

// Default Cross-Section (IPE 200)
export const DEFAULT_CROSS_SECTION: CrossSection = {
  name: 'IPE 200',
  width: 100,
  height: 200,
  area: 28.5e-4,  // m²
  inertia: 1943e-8,  // m⁴
  isCustom: false,
  key: 'IPE200',
  material: 'steel',
  sectionType: 'I-beam',
  webThickness: 5.6,
  flangeThickness: 8.5,
  sectionClass: 'auto'
}

export const GRAVITY_M_PER_S2: number = 9.81
export const N_PER_KN: number = 1000

// The one self-weight formula: Area (m²) × Density (kg/m³) × g → kN/m. Every caller goes through
// here so a density change can never leave one readout disagreeing with another.
export const calculateSelfWeightFromDensity = (areaM2: number, densityKgPerM3: number): number => {
  return (areaM2 * densityKgPerM3 * GRAVITY_M_PER_S2) / N_PER_KN
}

export const INITIAL_SELF_WEIGHT: number = calculateSelfWeightFromDensity(28.5e-4, MATERIAL_PRESETS.steel.density)

// Diagram Types
export const DIAGRAM_TYPES = [
  { value: 'moment', label: 'Moment', unit: 'kNm', color: '#0d6efd' },
  { value: 'shear', label: 'Shear Force', unit: 'kN', color: '#dc3545' },
  { value: 'displacement', label: 'Displacement', unit: 'mm', color: '#198754' },
  { value: 'axial', label: 'Axial Force', unit: 'kN', color: '#ffc107' },
  { value: 'axialDisp', label: 'Axial Displacement', unit: 'mm', color: '#6610f2' },
  { value: 'reactions', label: 'Reactions', unit: 'kN', color: '#6f42c1' }
] as const

// Tab Configuration
export const TABS = [
  { id: 'geometry', label: 'Geometry', icon: 'bi-rulers' },
  { id: 'materials', label: 'Materials', icon: 'bi-box-seam' },
  { id: 'loads', label: 'Loads', icon: 'bi-arrow-down-circle' },
  { id: 'analysis', label: 'Analysis', icon: 'bi-calculator' },
  { id: 'results', label: 'Results', icon: 'bi-graph-up' },
  { id: 'response', label: 'Response & UR', icon: 'bi-shield-check' }
] as const

// Load Case Types
export const LOAD_CASE_TYPES = [
  { value: 'Permanent', label: 'Permanent (G)', color: 'primary' },
  { value: 'Variable', label: 'Variable (Q)', color: 'success' },
  { value: 'Wind', label: 'Wind (W)', color: 'info' },
  { value: 'Snow', label: 'Snow (S)', color: 'secondary' }
] as const

// Support Types
export const SUPPORT_TYPES = [
  { value: 'Free', label: 'Free', icon: '○', color: '#6c757d' },
  { value: 'Roller', label: 'Roller', icon: '▽', color: '#ffc107' },
  { value: 'Pinned', label: 'Pinned', icon: '▼', color: '#28a745' },
  { value: 'Fixed', label: 'Fixed', icon: '■', color: '#dc3545' }
] as const

// Load Types
export const LOAD_TYPES = [
  { value: 'UniformLoad', label: 'Uniform Load', icon: 'bi-arrow-down' },
  { value: 'PointLoad', label: 'Point Load', icon: 'bi-arrow-down-circle-fill' },
  { value: 'TriangularRight', label: 'Triangular (Right)', icon: 'bi-triangle-fill' },
  { value: 'TriangularLeft', label: 'Triangular (Left)', icon: 'bi-triangle' },
  { value: 'AxialForce', label: 'Axial Force', icon: 'bi-arrow-left-right' }
] as const

// Validation Limits
export const VALIDATION_LIMITS = {
  span: {
    minLength: 0.1,  // m
    maxLength: 100,  // m
    minCount: 1,
    maxCount: 10
  },
  load: {
    minValue: 0.001,  // kN or kN/m
    maxValue: 10000,  // kN or kN/m
  },
  crossSection: {
    minWidth: 10,    // mm
    maxWidth: 2000,  // mm
    minHeight: 10,   // mm
    maxHeight: 2000  // mm
  }
} as const

// API Endpoints
export const API_ENDPOINTS = {
  analyze: '/api/smbeam/analyze',
  codeCheck: '/api/smbeam/code-check',
  response: '/api/smbeam/response',
  steelLibrary: '/api/libraries/steel-materials',
  timberLibrary: '/api/libraries/timber-materials',
  examples: '/api/examples'
} as const

// Result Display Options
export const RESULT_DISPLAY_OPTIONS = {
  valueDecimals: [2, 4] as const,
  valueDensity: {
    min: 0,
    max: 1,
    step: 0.1,
    default: 0.5
  },
  diagramScale: {
    min: 0.1,
    max: 5,
    step: 0.1,
    default: 1
  }
} as const

